import { useLayoutEffect, useRef, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
	faAngleDown,
	faAngleUp,
	faBan,
	faBoxArchive,
	faChartLine,
	faCode,
	faCopy,
	faDice,
	faHand,
	faList,
	faPlay,
	faRepeat,
	faTrashCan,
	faTrophy,
	faVolumeHigh,
	faWandMagicSparkles,
} from "@fortawesome/free-solid-svg-icons";
import cn from "classnames";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { setTabToggle } from "@/store/ses/sesScriptSlice";
import { selectClusterServer } from "@/store/serverSlice";
import { selectHintsEnabled, selectSesHints } from "@/store/hintsSlice";
import { useAccessRight, useTranslate } from "@/hooks";
import { determineScriptTypeByAction } from "@/helpers";
import { SES } from "@/constants/accessRights";
import { colorPrimary, colorPrimaryDisabled, colorRed, colorSecondary } from "@/constants/colors";
import { ActionType, ActionVariantType, IActionConfirmation, IActionInternalAsk, IActionInternalChat, IActionPhrase } from "@/types/ses/sesItemConfigTypes";
import { RequestStatus } from "@/types/statusTypes";
import { Hint } from "@/HOC";
import { AutocompleteWithIconSmall, LegendButton } from "@/ui";
import IconWithPopupInfo from "../../IconWithPopupInfo/IconWithPopupInfo";
import { ILegendProps } from "./Legend.props.";
import styles from "./Legend.module.scss";

const Legend = ({
	action,
	actionFor,
	idx,
	channel,
	isDisabled,
	selectExtendedAction,
	numberOfNonExistentCategories,
	showPhraseList,
	setShowPhraseList,
	setAnchorElCategoriesSettings,
	setAnchorElConditionsRun,
	isFilterActive,
	searchedDataElemIn,
	setShowCopyAndMoveForm,
	setShowAlertDialogDelAction,
	actionsArr,
	moveAction,
	handleSaveActionInStore,
}: ILegendProps): JSX.Element => {
	const [orderNumber, setOrderNumber] = useState<string | number>(idx + 1);
	const initialOrderNumber = useRef<number>(idx + 1);

	const [checkSetAnswered, setCheckSetAnswered] = useState<boolean>("setAnswered" in action && action.setAnswered ? action.setAnswered : false); // флаг "считать действие успешным прохождением точки" (только для действий КТ)
	const [checkSetConversion, setCheckSetConversion] = useState<boolean>("setConversion" in action && action.setConversion ? action.setConversion : false); // флаг "считать конверсию" (только для действий КТ)

	const [checkInterruptible, setCheckInterruptible] = useState<boolean>("interruptible" in action && action.interruptible ? action.interruptible : false); // прерываемое действие
	const [checkBreak, setCheckBreak] = useState<boolean>("break" in action && action.break ? action.break : false); // остановка выполнения действия
	const [checkReask, setCheckReask] = useState<boolean>(action.action === "declinedata" && action.reask ? action.reask : false); // перезапросить элемент
	const [checkGPT, setCheckGPT] = useState<boolean>(
		action.action === "say" && action.type === "internal" && action.service === "qas-ask" && action.useGPT === "yes" ? true : false
	); // использование gpt
	const [checkAddAlias, setCheckAddAlias] = useState<boolean>(
		action.action === "say" && action.type === "internal" && action.service === "qas-ask" && action.addAlias === "yes" ? true : false
	); // добавление вопроса в похожие в кэше, если похожая формулировка найдена
	const [selectActionType, setSelectActionType] = useState<ActionType>(action.action === "say" ? action.type : action.action === "confirmation" ? action.type : "linear"); // тип действия

	const [inputVoice, setInputVoice] = useState<string>(action.action === "say" ? action.voice : ""); // голос

	const dispatch = useAppDispatch();
	const { tts: clusterServerTts } = useAppSelector(selectClusterServer); // store - список моделей на сервере cluster
	const hintsEnabled = useAppSelector(selectHintsEnabled);
	const sesHints = useAppSelector(selectSesHints);

	const isAccess = useAccessRight(); // hook для проверки прав доступа
	const translate = useTranslate(); // hook для перевода текста

	// блок с несколькими кнопками
	const hasAnyButton = selectExtendedAction !== "goto" && selectExtendedAction !== "break" && selectExtendedAction !== "end";
	// блок с несколькими кнопками для КТ
	const hasAnyButtonOnlyEndpoint =
		actionFor.for === "endpoint" &&
		selectExtendedAction !== "qas-chat" &&
		selectExtendedAction !== "declinedata" &&
		selectExtendedAction !== "confirmation" &&
		selectExtendedAction !== "break" &&
		selectExtendedAction !== "goto";

	useLayoutEffect(() => {
		setOrderNumber(idx + 1);
	}, [idx]);

	// обработчик изменения порядкового номера действия
	const handleChangeActionsOrder = (): void => {
		if (orderNumber === idx + 1) return; // если значение не поменялось
		if (typeof orderNumber === "number") {
			if (orderNumber < 1) {
				setOrderNumber(initialOrderNumber.current);
			} else if (orderNumber > actionsArr.length) {
				moveAction(idx, actionsArr.length - 1);
				setOrderNumber(actionsArr.length);
				initialOrderNumber.current = actionsArr.length;
			} else {
				moveAction(idx, orderNumber > actionsArr.length ? actionsArr.length - 1 : orderNumber - 1);
				initialOrderNumber.current = orderNumber - 1;
			}
		} else if (orderNumber === "") {
			setOrderNumber(initialOrderNumber.current);
		}
	};

	// обработчик перемещения действия вверх
	const handleChangeActionsOrderActionUp = (): void => {
		if (typeof orderNumber === "number") {
			moveAction(idx, orderNumber - 2);
		}
	};

	// обработчик перемещения действия вниз
	const handleChangeActionsOrderActionDown = (): void => {
		if (typeof orderNumber === "number") {
			moveAction(idx, orderNumber);
		}
	};

	// обработчик изменения флага "считать действие успешным прохождением точки" (только для действий КТ, и то выборочно)
	const changeSetAnsweredHandler = (): void => {
		const value = !checkSetAnswered;
		setCheckSetAnswered(value);
		setCheckSetConversion(false); // убираем флаг конверсии при выключенном флаге прохождения КТ
		const data: { channel: string; idx: number; action: Partial<ActionVariantType> } = {
			channel,
			idx,
			action: { setAnswered: value, setConversion: false },
		};
		handleSaveActionInStore(data);
	};

	// обработчик изменения флага "считать конверсию" (только для действий КТ, и то выборочно)
	const changeSetConversionHandler = (): void => {
		const value = !checkSetConversion;
		setCheckSetConversion(value);
		const data: { channel: string; idx: number; action: Partial<ActionVariantType> } = {
			channel,
			idx,
			action: { setConversion: value },
		};
		handleSaveActionInStore(data);
	};

	// обработчик изменения флага прерываемого действия
	const changeInterruptibleHandler = (): void => {
		const value = !checkInterruptible;
		setCheckInterruptible(value);
		const data: { channel: string; idx: number; action: Partial<ActionVariantType> } = {
			channel,
			idx,
			action: { interruptible: value },
		};
		handleSaveActionInStore(data);
	};

	// обработчик break
	const changeBreakCheckboxHandler = (): void => {
		const value = !checkBreak;
		setCheckBreak(value);
		const data: { channel: string; idx: number; action: Partial<ActionVariantType> } = {
			channel,
			idx,
			action: { break: value },
		};
		handleSaveActionInStore(data);
	};

	// обработчик изменения флага перезапрашивания элемента
	const changeReackHandler = (): void => {
		const value = !checkReask;
		setCheckReask(value);
		const data: { channel: string; idx: number; action: Partial<ActionVariantType> } = {
			channel,
			idx,
			action: { reask: value },
		};
		handleSaveActionInStore(data);
	};

	// обработчик изменения использования генерации
	const changeGPTHandler = (): void => {
		const value = !checkGPT;
		setCheckGPT(value);
		sayInternalActionDataChangeHandler({ useGPT: value });
	};

	// обработчик изменения использования кэширования алиасов
	const changeAddAliasHandler = (): void => {
		const value = !checkAddAlias;
		setCheckAddAlias(value);
		sayInternalActionDataChangeHandler({ useAddAlias: value });
	};

	// обработчик изменения данных действия "Спросить llm" / "Искать по документам в qas" (say-internal)
	const sayInternalActionDataChangeHandler = ({ useGPT, useAddAlias }: Partial<{ useGPT: boolean; useAddAlias: boolean }>): void => {
		const gpt = useGPT ?? checkGPT;
		const addAlias = useAddAlias ?? checkAddAlias;
		const data: { channel: string; idx: number; action: Partial<IActionInternalChat | IActionInternalAsk> } = {
			channel,
			idx,
			action: {
				useGPT: selectExtendedAction === "qas-ask" ? (gpt ? "yes" : "no") : undefined,
				addAlias: selectExtendedAction === "qas-ask" ? (addAlias ? "yes" : "no") : undefined,
			},
		};
		handleSaveActionInStore(data);
	};

	// изменение типа фраз (последовательно/случайно)
	const toggleLinearRandomType = (): void => {
		if (selectActionType === "linear") changeActionTypeHandler("random");
		else if (selectActionType === "random") changeActionTypeHandler("linear");
	};

	// обработчик изменения типа действия
	const changeActionTypeHandler = (actionType: Extract<ActionType, "linear" | "random">): void => {
		setSelectActionType(actionType);
		const data: { channel: string; idx: number; action: Partial<IActionPhrase | IActionConfirmation> } = {
			channel,
			idx,
			action: { type: actionType },
		};
		handleSaveActionInStore(data);
	};

	// обработчик изменения голоса
	const changeVoiceHandler = (value: string | null): void => {
		const voice = value ? value : "";
		setInputVoice(voice);
		const data: { channel: string; idx: number; action: Partial<IActionPhrase | IActionConfirmation> } = {
			channel,
			idx,
			action: { voice: voice },
		};
		handleSaveActionInStore(data);
	};

	return (
		<div className={styles.legend}>
			<legend className={styles.legendLeft}>
				{/* порядковый номер действия */}
				<Hint hint={sesHints.hint_sesFieldActionOrder} position="top" offset={[0, -8]}>
					<input
						type="number"
						className={cn(styles.legendInput, {
							[styles.legendInputWithHint]: hintsEnabled,
						})}
						title={hintsEnabled ? "" : translate("input_indexNumber")}
						value={orderNumber}
						min={1}
						max={actionsArr.length}
						onBlur={handleChangeActionsOrder}
						onChange={(e) => {
							const newValue = e.target.value;
							setOrderNumber(newValue === "" ? "" : Number(newValue));
						}}
						onKeyDown={(e) => {
							if (e.key === "Enter") {
								e.preventDefault();
								handleChangeActionsOrder();
							}
						}}
						disabled={isDisabled}
					/>
				</Hint>
				{!isDisabled && (
					<Hint hint={sesHints.hint_sesBlockActionMoveControls} position="top" offset={[0, -8]}>
						<div className={styles.legendBlock} style={{ gap: "5px" }}>
							{/* переместить действие вверх */}
							<button
								type="button"
								className={styles.legendButton}
								title={translate("button_upInOrder")}
								disabled={idx + 1 === 1}
								onClick={handleChangeActionsOrderActionUp}
							>
								<FontAwesomeIcon icon={faAngleUp} size="lg" color={idx + 1 === 1 ? colorPrimaryDisabled : colorSecondary} />
							</button>
							{/* переместить действие вниз */}
							<button
								type="button"
								className={styles.legendButton}
								title={translate("button_downInOrder")}
								disabled={idx + 1 === actionsArr.length}
								onClick={handleChangeActionsOrderActionDown}
							>
								<FontAwesomeIcon icon={faAngleDown} size="lg" color={idx + 1 === actionsArr.length ? colorPrimaryDisabled : colorSecondary} />
							</button>
						</div>
					</Hint>
				)}
			</legend>

			<legend className={styles.legendRight}>
				{/* только для действий КТ, и то выборочно */}
				{hasAnyButtonOnlyEndpoint && (
					<div className={styles.legendBlock}>
						{/* считать действие успешным прохождением точки */}
						<button
							type="button"
							className={styles.legendButton}
							title={
								hintsEnabled
									? ""
									: checkSetAnswered
									? translate("checkbox_considerActionSuccessfulPassingEndpoint")
									: translate("checkbox_noConsiderActionSuccessfulPassingEndpoint")
							}
							disabled={isDisabled}
							onClick={changeSetAnsweredHandler}
						>
							<Hint hint={sesHints.hint_sesFieldSuccessFlag} position="top" offset={[0, -4]}>
								<FontAwesomeIcon icon={faTrophy} size="lg" color={checkSetAnswered ? colorSecondary : colorPrimary} />
							</Hint>
						</button>
						{/* подсчет конверсии */}
						<button
							type="button"
							className={styles.legendButton}
							title={hintsEnabled ? "" : checkSetConversion ? translate("checkbox_conversionActive") : translate("checkbox_conversionInactive")}
							disabled={isDisabled || !checkSetAnswered}
							onClick={changeSetConversionHandler}
						>
							<Hint hint={sesHints.hint_sesFieldConversionFlag} position="top" offset={[0, -4]}>
								<FontAwesomeIcon icon={faChartLine} size="lg" color={!checkSetAnswered ? colorPrimaryDisabled : checkSetConversion ? colorSecondary : colorPrimary} />
							</Hint>
						</button>
					</div>
				)}
				{hasAnyButton && (
					<div className={styles.legendBlock}>
						{/* прерываемое действие */}
						{(selectExtendedAction === "say" ||
							selectExtendedAction === "confirmation" ||
							selectExtendedAction === "sendfile" ||
							selectExtendedAction === "qas-chat" ||
							selectExtendedAction === "qas-ask" ||
							selectExtendedAction === "external") && (
							<button
								type="button"
								className={styles.legendButton}
								title={hintsEnabled ? "" : checkInterruptible ? translate("checkbox_interruptible") : translate("checkbox_noInterruptible")}
								disabled={isDisabled}
								onClick={changeInterruptibleHandler}
							>
								<Hint hint={sesHints.hint_sesFieldInterruptible} position="top" offset={[0, -4]}>
									<FontAwesomeIcon icon={faHand} size="lg" color={checkInterruptible ? colorSecondary : colorPrimary} />
								</Hint>
							</button>
						)}
						{/* остановка выполнения действий */}
						<button
							type="button"
							className={styles.legendButton}
							title={hintsEnabled ? "" : checkBreak ? translate("checkbox_break") : translate("checkbox_noBreak")}
							disabled={isDisabled}
							onClick={changeBreakCheckboxHandler}
						>
							<Hint hint={sesHints.hint_sesActionStopExecution} position="top" offset={[0, -4]}>
								<FontAwesomeIcon icon={faBan} size="lg" color={checkBreak ? colorSecondary : colorPrimary} />
							</Hint>
						</button>
						{/* перезапросить элемент */}
						{selectExtendedAction === "declinedata" && (
							<button
								type="button"
								className={styles.legendButton}
								title={checkReask ? translate("checkbox_reaskElement") : translate("checkbox_noReaskElement")}
								disabled={isDisabled}
								onClick={changeReackHandler}
							>
								<FontAwesomeIcon icon={faRepeat} size="lg" color={checkReask ? colorSecondary : colorPrimary} />
							</button>
						)}
						{selectExtendedAction === "qas-ask" && (
							<>
								{/* генерация */}
								<button
									type="button"
									className={styles.legendButton}
									title={hintsEnabled ? "" : checkGPT ? translate("checkbox_generationActive") : translate("checkbox_generationInacctive")}
									disabled={isDisabled}
									onClick={changeGPTHandler}
								>
									<Hint hint={sesHints.hint_sesFieldGenerationEnabled} position="top" offset={[0, -4]}>
										<FontAwesomeIcon icon={faWandMagicSparkles} size="lg" color={checkGPT ? colorSecondary : colorPrimary} />
									</Hint>
								</button>
								{/* кэширование вопросов */}
								<button
									type="button"
									className={styles.legendButton}
									title={
										hintsEnabled ? "" : checkAddAlias ? translate("checkbox_cachingTheQuestionAliaseActive") : translate("checkbox_cachingTheQuestionAliaseInactive")
									}
									disabled={isDisabled}
									onClick={changeAddAliasHandler}
								>
									<Hint hint={sesHints.hint_sesFieldCachePhrases} position="top" offset={[0, -4]}>
										<FontAwesomeIcon icon={faBoxArchive} size="lg" color={checkAddAlias ? colorSecondary : colorPrimary} />
									</Hint>
								</button>
							</>
						)}
						{/* фразы последовательно/случайно */}
						{(selectExtendedAction === "say" || selectExtendedAction === "confirmation") && (
							<button
								type="button"
								className={styles.legendButton}
								title={hintsEnabled ? "" : selectActionType === "random" ? translate("checkbox_actionRandom") : translate("checkbox_actionLinear")}
								disabled={isDisabled}
								onClick={toggleLinearRandomType}
							>
								<Hint hint={sesHints.hint_sesFieldPhraseOutputType} position="top" offset={[0, -4]}>
									<FontAwesomeIcon icon={faDice} size="lg" color={selectActionType === "random" ? colorSecondary : colorPrimary} />
								</Hint>
							</button>
						)}
					</div>
				)}

				{/* голос */}
				{(selectExtendedAction === "say" ||
					selectExtendedAction === "confirmation" ||
					selectExtendedAction === "qas-chat" ||
					selectExtendedAction === "qas-ask" ||
					selectExtendedAction === "external") && (
					<AutocompleteWithIconSmall
						value={inputVoice}
						options={["", ...clusterServerTts.modelList]}
						handleChange={changeVoiceHandler}
						isLoading={clusterServerTts.status === RequestStatus.LOADING}
						isDisabled={isDisabled}
						fontSize={9}
						height="15px"
						ml={0}
						hint={"hint_actionVoice"}
						icon={faVolumeHigh}
					/>
				)}

				{/* открыть редактор */}
				{selectExtendedAction === "external" && isAccess([SES.SCRIPT_LIST, SES.SCRIPT_GET]) && (
					<LegendButton
						title={translate("button_openEditor")}
						onClick={() =>
							dispatch(
								setTabToggle({
									isOpen: true,
									scriptType: determineScriptTypeByAction(actionFor),
									scriptId: action.action === "say" && action.type === "external" ? action.script : "",
								})
							)
						}
					>
						<FontAwesomeIcon icon={faCode} size="1x" color={colorSecondary} />
					</LegendButton>
				)}

				{/* показать полный список фраз */}
				{(selectExtendedAction === "say" || selectExtendedAction === "confirmation") && (
					<LegendButton className={styles.legendButtonExtended} title={translate("button_showPhraseList")} onClick={() => setShowPhraseList((prev) => !prev)}>
						<div className={styles.legendButtonExtendedBlock}>
							<FontAwesomeIcon icon={faList} color={showPhraseList ? colorSecondary : colorPrimary} />
							{"text" in action && action.text.length > 1 && <span style={{ color: showPhraseList ? colorSecondary : colorPrimary }}>({action.text.length})</span>}
						</div>
					</LegendButton>
				)}

				{/* условия присвоения категорий */}
				{selectExtendedAction === "qas-ask" && (
					<LegendButton
						className={cn(styles.legendButtonExtended, {
							[styles.legendButtonExtendedFilter]: searchedDataElemIn.conditionsForAssigningCategories.includes(action.actionId ?? "none") && isFilterActive,
						})}
						warning={numberOfNonExistentCategories > 0}
						onClick={(e) => setAnchorElCategoriesSettings(e.currentTarget)}
					>
						<Hint hint={sesHints.hint_sesConfigureCategoryAssignmentConditions} position="top" offset={[0, -4]}>
							<div className={styles.legendButtonExtendedBlock}>
								{numberOfNonExistentCategories > 0 && (
									<IconWithPopupInfo iconSize="sm" iconColor={colorRed}>
										{`${translate("title_nonExistentCategoriesAreUsed")}: ${numberOfNonExistentCategories} ${translate("title_pcs")}`}
									</IconWithPopupInfo>
								)}
								{translate("button_conditionsForAssigningCategories")}
							</div>
						</Hint>
					</LegendButton>
				)}

				{/* условия запуска действия */}
				{actionFor.for !== "endpointEntry" && (
					<LegendButton
						className={cn(styles.legendButtonExtended, {
							[styles.legendButtonExtendedFilter]: searchedDataElemIn.runConditions.includes(action.actionId ?? "none") && isFilterActive,
						})}
						title={translate("button_runConditions")}
						onClick={(e) => setAnchorElConditionsRun(e.currentTarget)}
					>
						<Hint hint={sesHints.hint_sesConfigureActionConditions} position="right" offset={[0, -4]}>
							<div className={styles.legendButtonExtendedBlock}>
								<FontAwesomeIcon icon={faPlay} />
								{"conditions" in action && action.conditions && action.conditions?.length > 0 && <span>({action.conditions.length})</span>}
							</div>
						</Hint>
					</LegendButton>
				)}

				{!isDisabled && (
					<Hint hint={sesHints.hint_sesBlockActionControls} position="top" offset={[0, -4]}>
						<div className={styles.legendBlock}>
							{/* копировать */}
							<button
								type="button"
								className={styles.legendButton}
								onClick={() => setShowCopyAndMoveForm({ isShown: true, type: "copy" })}
								title={hintsEnabled ? "" : translate("button_copy")}
							>
								<svg xmlns="http://www.w3.org/2000/svg" height="10" viewBox="0 0 448 512">
									{/* !Font Awesome Free 6.5.2 by @fontawesome - https://fontawesome.com License - https://fontawesome.com/license/free Copyright 2024 Fonticons, Inc. */}
									<path
										fill="#918c8c"
										d="M384 336H192c-8.8 0-16-7.2-16-16V64c0-8.8 7.2-16 16-16l140.1 0L400 115.9V320c0 8.8-7.2 16-16 16zM192 384H384c35.3 0 64-28.7 64-64V115.9c0-12.7-5.1-24.9-14.1-33.9L366.1 14.1c-9-9-21.2-14.1-33.9-14.1H192c-35.3 0-64 28.7-64 64V320c0 35.3 28.7 64 64 64zM64 128c-35.3 0-64 28.7-64 64V448c0 35.3 28.7 64 64 64H256c35.3 0 64-28.7 64-64V416H272v32c0 8.8-7.2 16-16 16H64c-8.8 0-16-7.2-16-16V192c0-8.8 7.2-16 16-16H96V128H64z"
									/>
								</svg>
							</button>
							{/* ограничение на событие робота */}
							{!(actionFor.for === "robotEvent" && channel === "voice" && (actionFor.event === "interrupt" || actionFor.event === "answer delay")) && (
								<>
									{/* переместить */}
									<button
										type="button"
										className={styles.legendButton}
										onClick={() => setShowCopyAndMoveForm({ isShown: true, type: "move" })}
										title={hintsEnabled ? "" : translate("button_move")}
									>
										<FontAwesomeIcon icon={faCopy} size="lg" color={colorPrimary} />
									</button>
									{/* удалить */}
									<button
										type="button"
										className={styles.legendButton}
										onClick={() => setShowAlertDialogDelAction(true)}
										title={hintsEnabled ? "" : translate("button_deleteAction")}
									>
										<FontAwesomeIcon icon={faTrashCan} color={colorRed} size="lg" />
									</button>
								</>
							)}
						</div>
					</Hint>
				)}
			</legend>
		</div>
	);
};

export default Legend;
